import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite, list } from '../lib/api'
import { useInvoices } from '../lib/queries'
import { Empty, PageHead, Skel, Stat } from '../components/ui'
import { money, number, thaiDateTime } from '../lib/format'

/**
 * Payment-webhook reconciliation (part 140).
 *
 * The page never calculates a verdict. It imports provider events, asks the server to match
 * them, and then shows the stored result: which reference matched which invoice, which one was
 * a duplicate, and how many satang a shortfall is missing. That keeps the screen and the
 * database from disagreeing after a retry, and it means the reason shown here is the same
 * reason a second reviewer sees next week.
 *
 * All money on the wire is integer satang. The import textarea accepts baht for humans and
 * converts to satang with integer parsing before the request; no amount is ever a float.
 */

interface ReconBatch {
  id: string
  label?: string
  source: string
  provider: string
  currency: string
  status: string
  line_count: number
  matched_count: number
  duplicate_count: number
  shortfall_count: number
  overpayment_count: number
  unmatched_count: number
  ignored_count: number
  gross_satang: number
  fee_satang: number
  matched_satang: number
  credit_satang: number
  shortfall_satang: number
  created_by?: string
  created_at: string
  completed_at?: string
}

interface ReconLine {
  id: string
  batch_id: string
  batch_label?: string
  row_no: number
  provider: string
  provider_ref: string
  invoice_ref?: string
  invoice_id?: string
  invoice_number?: string
  invoice_currency?: string
  amount_satang: number
  fee_satang: number
  net_satang: number
  currency: string
  fx_rate_bp: number
  status: string
  occurred_at: string
  outcome: string
  reason_code: string
  reason: string
  applied_satang: number
  credit_satang: number
  shortfall_satang: number
  fx_diff_satang: number
  fx_diff_bp: number
}

interface ReconBatchView {
  batch: ReconBatch
  lines: ReconLine[]
}

interface ReconMatch {
  invoice_number: string
  applied_satang: number
  credit_satang: number
  shortfall_satang: number
  match_kind: string
  status: string
  note?: string
}

interface ReconResolution {
  line: ReconLine
  match: ReconMatch
  batch: ReconBatch
}

interface ImportLine {
  provider_ref: string
  invoice_ref: string
  amount_satang: number
  fee_satang: number
  currency: string
  fx_rate_bp: number
  status: string
}

const BATCHES_KEY = ['recon-batches'] as const
const batchKey = (id: string) => ['recon-batch', id] as const
const unmatchedKey = (id: string) => ['recon-unmatched', id] as const

const sourceLabel: Record<string, string> = {
  payment_events: 'เหตุการณ์จากผู้ให้บริการ',
  webhook_export: 'ไฟล์ส่งออก webhook',
  bank_statement: 'รายการเดินบัญชีธนาคาร',
  manual: 'นำเข้าด้วยมือ',
}

const statusLabel: Record<string, string> = {
  imported: 'นำเข้าแล้ว',
  running: 'กำลังจับคู่',
  completed: 'จับคู่แล้ว',
  failed: 'ล้มเหลว',
}

const outcomeLabel: Record<string, string> = {
  pending: 'รอจับคู่',
  matched: 'จับคู่แล้ว',
  duplicate: 'ซ้ำ',
  shortfall: 'ชำระไม่ครบ',
  overpayment: 'ชำระเกิน',
  unmatched: 'ไม่พบใบแจ้งหนี้',
  ignored: 'ไม่นำมาคิด',
}

function outcomeColor(outcome: string): string | undefined {
  switch (outcome) {
    case 'matched':
      return 'var(--color-emerald)'
    case 'duplicate':
    case 'shortfall':
    case 'unmatched':
      return 'var(--color-crimson)'
    case 'overpayment':
      return 'var(--color-amber)'
    default:
      return undefined
  }
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

/** Integer satang from a baht string such as "2,033.00". No floating point. */
function parseSatang(raw: string): number | null {
  const s = raw.replace(/,/g, '').trim()
  const m = /^(\d+)(?:\.(\d{1,2}))?$/.exec(s)
  if (!m) return null
  const whole = Number(m[1])
  const frac = Number(((m[2] ?? '') + '00').slice(0, 2))
  if (!Number.isSafeInteger(whole)) return null
  const satang = whole * 100 + frac
  return Number.isSafeInteger(satang) ? satang : null
}

/** Integer basis points from a rate string such as "35.42" -> 354200. */
function parseRateBP(raw: string): number | null {
  const s = raw.replace(/,/g, '').trim()
  const m = /^(\d+)(?:\.(\d{1,4}))?$/.exec(s)
  if (!m) return null
  const whole = Number(m[1])
  const frac = Number(((m[2] ?? '') + '0000').slice(0, 4))
  if (!Number.isSafeInteger(whole)) return null
  const bp = whole * 10000 + frac
  return Number.isSafeInteger(bp) ? bp : null
}

function amountText(satang: number, currency: string): string {
  if (!currency || currency === 'THB') return money(satang)
  return `${currency} ${(satang / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

/**
 * CSV columns: provider_ref, invoice_ref, amount_baht, fee_baht, currency, fx_rate, status.
 * A header row is skipped. Blank fee/currency/fx/status take the form defaults.
 */
function parseImportCSV(text: string, defaultCurrency: string): ImportLine[] {
  const out: ImportLine[] = []
  const rows = text.split(/\r?\n/)
  for (let i = 0; i < rows.length; i += 1) {
    const raw = rows[i].trim()
    if (raw === '' || raw.startsWith('#')) continue
    const cells = raw.split(',').map((cell) => cell.trim())
    if (cells[0]?.toLowerCase() === 'provider_ref') continue
    const at = `บรรทัด ${i + 1}`
    if (cells.length < 3 || !cells[0] || !cells[1]) {
      throw new Error(`${at}: ต้องมี provider_ref, invoice_ref และจำนวนเงิน คั่นด้วยจุลภาค`)
    }
    const amount = parseSatang(cells[2] ?? '')
    if (amount === null || amount <= 0) {
      throw new Error(`${at}: จำนวนเงินต้องเป็นตัวเลขมากกว่าศูนย์ เช่น 2033.00`)
    }
    let fee = 0
    if ((cells[3] ?? '') !== '') {
      const parsed = parseSatang(cells[3])
      if (parsed === null || parsed < 0) throw new Error(`${at}: ค่าธรรมเนียมไม่ถูกต้อง`)
      fee = parsed
    }
    if (fee >= amount) throw new Error(`${at}: ค่าธรรมเนียมต้องน้อยกว่าจำนวนเงิน`)
    const currency = (cells[4] || defaultCurrency).toUpperCase()
    if (!/^[A-Z]{3}$/.test(currency)) throw new Error(`${at}: สกุลเงินต้องเป็นตัวอักษร 3 ตัว`)
    let fxRate = 0
    if ((cells[5] ?? '') !== '') {
      const parsed = parseRateBP(cells[5])
      if (parsed === null || parsed <= 0) throw new Error(`${at}: อัตราแลกเปลี่ยนไม่ถูกต้อง`)
      fxRate = parsed
    }
    out.push({
      provider_ref: cells[0],
      invoice_ref: cells[1],
      amount_satang: amount,
      fee_satang: fee,
      currency,
      fx_rate_bp: fxRate,
      status: (cells[6] || 'succeeded').toLowerCase(),
    })
  }
  if (out.length === 0) throw new Error('ยังไม่มีรายการนำเข้า')
  return out
}

export function Reconciliation() {
  const qc = useQueryClient()
  const invoices = useInvoices()

  const [selectedID, setSelectedID] = useState('')
  const [source, setSource] = useState('payment_events')
  const [provider, setProvider] = useState('stripe')
  const [label, setLabel] = useState('')
  const [currency, setCurrency] = useState('THB')
  const [csv, setCSV] = useState('')
  const [formError, setFormError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const [resolving, setResolving] = useState<ReconLine | null>(null)
  const [invoiceID, setInvoiceID] = useState('')
  const [note, setNote] = useState('')
  const [resolveError, setResolveError] = useState<string | null>(null)

  const batches = useQuery({
    queryKey: BATCHES_KEY,
    queryFn: () => list<ReconBatch>('/v1/recon/batches?limit=100'),
    staleTime: 15_000,
  })
  const rows = batches.data ?? []

  useEffect(() => {
    // Only choose a default; never override an explicit selection (an import selects the new
    // batch immediately, before the list has refetched).
    if (selectedID) return
    if (rows.length > 0) setSelectedID(rows[0].id)
  }, [rows, selectedID])

  const detail = useQuery({
    queryKey: batchKey(selectedID),
    queryFn: () => api<ReconBatchView>(`/v1/recon/batches/${selectedID}`),
    enabled: Boolean(selectedID),
    staleTime: 10_000,
  })

  const unmatched = useQuery({
    queryKey: unmatchedKey(selectedID),
    queryFn: () => list<ReconLine>(`/v1/recon/unmatched?batch_id=${selectedID}&limit=200`),
    enabled: Boolean(selectedID),
    staleTime: 10_000,
  })

  const invalidate = (batchID: string) => {
    void qc.invalidateQueries({ queryKey: BATCHES_KEY })
    void qc.invalidateQueries({ queryKey: batchKey(batchID) })
    void qc.invalidateQueries({ queryKey: unmatchedKey(batchID) })
  }

  const importBatch = useMutation({
    mutationFn: (input: {
      source: string
      provider: string
      label: string
      currency: string
      lines: ImportLine[]
    }) => apiWrite<ReconBatchView>('/v1/recon/batches', 'POST', input),
    onSuccess: (view) => {
      setSelectedID(view.batch.id)
      setNotice(`นำเข้า ${view.batch.line_count} รายการแล้ว ยังไม่มีการจับคู่ · ตรวจรายการแล้วกด “จับคู่ทั้งหมด”`)
      setFormError(null)
      setCSV('')
      invalidate(view.batch.id)
    },
  })

  const runBatch = useMutation({
    mutationFn: (batchID: string) =>
      apiWrite<ReconBatchView>(`/v1/recon/batches/${batchID}/run`, 'POST', {}),
    onSuccess: (view) => {
      setNotice(
        `จับคู่เสร็จ: ตรง ${view.batch.matched_count} · ซ้ำ ${view.batch.duplicate_count} · ` +
        `ขาด ${view.batch.shortfall_count} · เกิน ${view.batch.overpayment_count} · ` +
        `ไม่พบใบแจ้งหนี้ ${view.batch.unmatched_count}`,
      )
      invalidate(view.batch.id)
    },
  })

  const resolveLine = useMutation({
    mutationFn: (input: { lineID: string; invoiceID: string; note: string }) =>
      apiWrite<ReconResolution>(`/v1/recon/lines/${input.lineID}/resolve`, 'POST', {
        invoice_id: input.invoiceID,
        note: input.note,
      }),
    onSuccess: (result) => {
      setNotice(
        `จับคู่ด้วยมือกับ ${result.match.invoice_number} แล้ว · ` +
        `ใช้ยอด ${money(result.match.applied_satang)}` +
        (result.match.shortfall_satang > 0 ? ` · รับทราบยอดขาด ${money(result.match.shortfall_satang)}` : ''),
      )
      setResolving(null)
      setNote('')
      setResolveError(null)
      invalidate(result.batch.id)
    },
  })

  function submitImport() {
    setFormError(null)
    setNotice(null)
    if (!provider.trim()) {
      setFormError('ต้องระบุผู้ให้บริการ')
      return
    }
    if (!/^[A-Za-z]{3}$/.test(currency.trim())) {
      setFormError('สกุลเงินต้องเป็นตัวอักษร 3 ตัว')
      return
    }
    let lines: ImportLine[]
    try {
      lines = parseImportCSV(csv, currency.trim().toUpperCase())
    } catch (err) {
      setFormError(errorText(err, 'อ่านรายการนำเข้าไม่สำเร็จ'))
      return
    }
    importBatch.mutate({ source, provider: provider.trim(), label: label.trim(), currency: currency.trim().toUpperCase(), lines })
  }

  function beginResolve(line: ReconLine) {
    const match = (invoices.data ?? []).find((inv) => inv.number === line.invoice_ref)
    setResolving(line)
    setInvoiceID(match?.id ?? invoices.data?.[0]?.id ?? '')
    setNote('')
    setResolveError(null)
  }

  function submitResolve() {
    if (!resolving) return
    if (!invoiceID) {
      setResolveError('เลือกใบแจ้งหนี้ก่อนบันทึก')
      return
    }
    if (note.trim().length < 3) {
      setResolveError('ระบุเหตุผลอย่างน้อย 3 ตัวอักษร เพื่อให้ผู้ตรวจสอบคนถัดไปเข้าใจ')
      return
    }
    setResolveError(null)
    resolveLine.mutate({ lineID: resolving.id, invoiceID, note: note.trim() })
  }

  const selectedBatch = detail.data?.batch ?? rows.find((row) => row.id === selectedID)
  const lines = detail.data?.lines ?? []
  const queue = unmatched.data ?? []
  const busy = importBatch.isPending || runBatch.isPending

  const stats = useMemo(() => {
    if (!selectedBatch) return null
    return [
      { label: 'รายการทั้งหมด', value: number(selectedBatch.line_count) },
      { label: 'จับคู่แล้ว', value: number(selectedBatch.matched_count) },
      { label: 'ซ้ำ', value: number(selectedBatch.duplicate_count) },
      { label: 'ชำระไม่ครบ', value: number(selectedBatch.shortfall_count) },
      { label: 'ชำระเกิน', value: number(selectedBatch.overpayment_count) },
      { label: 'ไม่พบใบแจ้งหนี้', value: number(selectedBatch.unmatched_count) },
    ]
  }, [selectedBatch])

  return (
    <>
      <PageHead
        title="กระทบยอดการชำระเงิน"
        sub="นำเหตุการณ์ชำระเงินจากผู้ให้บริการมาเทียบกับใบแจ้งหนี้ด้วยรหัสอ้างอิงและจำนวนเงิน ตรวจรายการซ้ำ ยอดขาด ค่าธรรมเนียม และส่วนต่างอัตราแลกเปลี่ยน"
      />

      {notice ? (
        <div className="card card-pad" role="status" style={{ marginBottom: 16, borderColor: 'var(--color-hairline-strong)' }}>
          <span className="dim" style={{ fontSize: 12.5 }}>{notice}</span>
        </div>
      ) : null}

      <div className="card card-pad">
        <div className="row" style={{ marginBottom: 12 }}>
          <span className="card-label">นำเข้ารอบใหม่</span>
          <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
            หนึ่งรอบไม่เกิน 500 รายการ · ยังไม่มีการจับคู่จนกว่าจะกดปุ่มจับคู่
          </span>
        </div>

        <div className="grid-2">
          <div className="field">
            <label htmlFor="recon-source">แหล่งที่มา</label>
            <select id="recon-source" className="input" value={source} onChange={(e) => setSource(e.target.value)}>
              {Object.entries(sourceLabel).map(([value, text]) => (
                <option key={value} value={value}>{text}</option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="recon-provider">ผู้ให้บริการ</label>
            <select id="recon-provider" className="input" value={provider} onChange={(e) => setProvider(e.target.value)}>
              <option value="stripe">Stripe</option>
              <option value="omise">Omise</option>
              <option value="bank">ธนาคาร</option>
              <option value="mock">ระบบจำลอง</option>
            </select>
          </div>
          <div className="field">
            <label htmlFor="recon-label">ชื่อรอบนำเข้า</label>
            <input
              id="recon-label"
              className="input"
              value={label}
              maxLength={160}
              placeholder="เช่น รายการโอน 3 พ.ย. 2569"
              onChange={(e) => setLabel(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="recon-currency">สกุลเงินเริ่มต้น</label>
            <input
              id="recon-currency"
              className="input tnum"
              value={currency}
              maxLength={3}
              onChange={(e) => setCurrency(e.target.value.toUpperCase())}
            />
          </div>
        </div>

        <div className="field" style={{ marginTop: 12 }}>
          <label htmlFor="recon-csv">รายการที่นำเข้า</label>
          <textarea
            id="recon-csv"
            className="input tnum"
            style={{ minHeight: 118, fontFamily: 'var(--font-mono, monospace)', fontSize: 12 }}
            value={csv}
            onChange={(e) => setCSV(e.target.value)}
            placeholder={'provider_ref,invoice_ref,amount_baht,fee_baht,currency,fx_rate,status\nPAY-1001,INV-2026-001001,2033.00,0,THB,,\nPAY-1002,INV-2026-001002,35470.00,50.00,USD,,'}
          />
          <div className="dim" style={{ fontSize: 11, marginTop: 6, lineHeight: 1.7 }}>
            คั่นด้วยจุลภาค · จำนวนเงินเป็นบาทและมีทศนิยมไม่เกิน 2 ตำแหน่ง · อัตราแลกเปลี่ยนมีทศนิยมไม่เกิน 4 ตำแหน่ง
            (เว้นว่างได้เมื่อสกุลเงินตรงกับใบแจ้งหนี้) · บรรทัดที่ขึ้นต้นด้วย # จะถูกข้าม
          </div>
        </div>

        {formError ? <div className="form-error" style={{ marginTop: 12 }}>{formError}</div> : null}

        <div className="row" style={{ gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
          <button className="btn btn-primary" onClick={submitImport} disabled={busy}>
            {importBatch.isPending ? 'กำลังนำเข้า…' : 'นำเข้ารายการ'}
          </button>
          <button
            className="btn"
            onClick={() => selectedID && runBatch.mutate(selectedID)}
            disabled={busy || !selectedID}
          >
            {runBatch.isPending ? 'กำลังจับคู่…' : 'จับคู่ทั้งหมด'}
          </button>
          {selectedBatch ? (
            <span className="dim" style={{ fontSize: 11.5, alignSelf: 'center' }}>
              รอบที่เลือก: {selectedBatch.label || selectedBatch.id.slice(0, 8)}
            </span>
          ) : null}
        </div>

        {importBatch.isError ? (
          <div className="form-error" style={{ marginTop: 12 }}>{errorText(importBatch.error, 'นำเข้าไม่สำเร็จ')}</div>
        ) : null}
        {runBatch.isError ? (
          <div className="form-error" style={{ marginTop: 12 }}>{errorText(runBatch.error, 'จับคู่ไม่สำเร็จ')}</div>
        ) : null}
      </div>

      <div className="card card-pad" style={{ marginTop: 22 }}>
        <div className="row" style={{ marginBottom: 12 }}>
          <span className="card-label">รอบนำเข้า</span>
          <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>เลือกแถวเพื่อดูรายละเอียดและรายการที่ยังไม่จับคู่</span>
        </div>

        {batches.isLoading ? <Skel h={160} /> : batches.isError ? (
          <div className="form-error">{errorText(batches.error, 'โหลดรอบนำเข้าไม่สำเร็จ')}</div>
        ) : rows.length === 0 ? (
          <div className="dim" style={{ fontSize: 12.5, lineHeight: 1.8 }}>
            ยังไม่มีรอบนำเข้า · วางรายการด้านบนแล้วกด “นำเข้ารายการ” เพื่อเริ่มต้น
          </div>
        ) : (
          <div className="table-wrap">
            <table className="table data">
              <thead>
                <tr>
                  <th>รอบนำเข้า</th>
                  <th style={{ width: 130 }}>แหล่งที่มา</th>
                  <th style={{ width: 110 }}>ผู้ให้บริการ</th>
                  <th style={{ width: 110 }}>สถานะ</th>
                  <th style={{ width: 80, textAlign: 'right' }}>รายการ</th>
                  <th style={{ width: 80, textAlign: 'right' }}>ตรง</th>
                  <th style={{ width: 80, textAlign: 'right' }}>ซ้ำ</th>
                  <th style={{ width: 100, textAlign: 'right' }}>ขาด</th>
                  <th style={{ width: 140 }}>นำเข้าเมื่อ</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.id}
                    onClick={() => { setSelectedID(row.id); setResolving(null); setNotice(null) }}
                    style={{
                      cursor: 'pointer',
                      background: row.id === selectedID ? 'var(--color-wash)' : undefined,
                    }}
                  >
                    <td>
                      <div>{row.label || `รอบ ${row.id.slice(0, 8)}`}</div>
                      <div className="dim" style={{ fontSize: 11 }}>
                        {row.completed_at ? `จับคู่เมื่อ ${thaiDateTime(row.completed_at)}` : 'ยังไม่จับคู่'}
                      </div>
                    </td>
                    <td>{sourceLabel[row.source] ?? row.source}</td>
                    <td>{row.provider}</td>
                    <td>
                      <span
                        className="chip"
                        style={{ color: row.status === 'completed' ? 'var(--color-emerald)' : undefined }}
                      >
                        {statusLabel[row.status] ?? row.status}
                      </span>
                    </td>
                    <td className="tnum" style={{ textAlign: 'right' }}>{number(row.line_count)}</td>
                    <td className="tnum" style={{ textAlign: 'right' }}>{number(row.matched_count)}</td>
                    <td className="tnum" style={{ textAlign: 'right', color: row.duplicate_count > 0 ? 'var(--color-crimson)' : undefined }}>
                      {number(row.duplicate_count)}
                    </td>
                    <td className="tnum" style={{ textAlign: 'right' }}>
                      {row.shortfall_count > 0 ? money(row.shortfall_satang) : '—'}
                    </td>
                    <td className="dim" style={{ fontSize: 11 }}>{thaiDateTime(row.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selectedBatch ? (
        <>
          <div className="row" style={{ gap: 24, marginTop: 22, flexWrap: 'wrap' }}>
            {(stats ?? []).map((stat) => (
              <Stat key={stat.label} label={stat.label} value={stat.value} />
            ))}
          </div>

          <div className="card card-pad" style={{ marginTop: 22 }}>
            <div className="row" style={{ marginBottom: 12 }}>
              <span className="card-label">รายการในรอบ</span>
              <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
                เงินที่รับ {money(selectedBatch.gross_satang)} · ค่าธรรมเนียม {money(selectedBatch.fee_satang)} ·
                เครดิตส่วนเกิน {money(selectedBatch.credit_satang)} · ยอดขาด {money(selectedBatch.shortfall_satang)}
              </span>
            </div>

            {detail.isLoading ? <Skel h={240} /> : detail.isError ? (
              <div className="form-error">{errorText(detail.error, 'โหลดรายละเอียดรอบไม่สำเร็จ')}</div>
            ) : lines.length === 0 ? (
              <Empty title="รอบนี้ยังไม่มีรายการ" body="นำเข้ารายการก่อน แล้วจึงเรียกจับคู่" />
            ) : (
              <div className="table-wrap">
                <table className="table data">
                  <thead>
                    <tr>
                      <th style={{ width: 52, textAlign: 'right' }}>แถว</th>
                      <th>รหัสผู้ให้บริการ</th>
                      <th>ใบแจ้งหนี้</th>
                      <th style={{ width: 140, textAlign: 'right' }}>ยอดที่ส่งมา</th>
                      <th style={{ width: 120, textAlign: 'right' }}>ค่าธรรมเนียม</th>
                      <th style={{ width: 130, textAlign: 'right' }}>ยอดสุทธิ</th>
                      <th style={{ width: 120 }}>ผล</th>
                      <th>เหตุผล</th>
                      <th style={{ width: 130, textAlign: 'right' }}>ยอดที่รับ</th>
                      <th style={{ width: 130, textAlign: 'right' }}>ขาด</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((line) => (
                      <tr key={line.id}>
                        <td className="tnum" style={{ textAlign: 'right' }}>{line.row_no}</td>
                        <td>
                          <div className="tnum" style={{ fontSize: 12 }}>{line.provider_ref}</div>
                          <div className="dim" style={{ fontSize: 11 }}>
                            {line.provider} · {line.status}
                            {line.fee_satang > 0 ? ` · หักค่าธรรมเนียม` : ''}
                          </div>
                        </td>
                        <td>
                          <div>{line.invoice_number || line.invoice_ref || '—'}</div>
                          {line.invoice_currency && line.invoice_currency !== line.currency ? (
                            <div className="dim" style={{ fontSize: 11 }}>
                              {line.currency} → {line.invoice_currency} @ {(line.fx_rate_bp / 10000).toFixed(4)}
                            </div>
                          ) : null}
                        </td>
                        <td className="tnum" style={{ textAlign: 'right' }}>{amountText(line.amount_satang, line.currency)}</td>
                        <td className="tnum" style={{ textAlign: 'right' }}>
                          {line.fee_satang > 0 ? amountText(line.fee_satang, line.currency) : '—'}
                        </td>
                        <td className="tnum" style={{ textAlign: 'right' }}>{amountText(line.net_satang, line.currency)}</td>
                        <td>
                          <span className="chip" style={{ color: outcomeColor(line.outcome) }}>
                            {outcomeLabel[line.outcome] ?? line.outcome}
                          </span>
                        </td>
                        <td style={{ fontSize: 12, lineHeight: 1.55 }}>{line.reason || '—'}</td>
                        <td className="tnum" style={{ textAlign: 'right' }}>
                          {line.applied_satang > 0 ? amountText(line.applied_satang, line.invoice_currency || line.currency) : '—'}
                        </td>
                        <td className="tnum" style={{ textAlign: 'right', color: line.shortfall_satang > 0 ? 'var(--color-crimson)' : undefined }}>
                          {line.shortfall_satang > 0 ? amountText(line.shortfall_satang, line.invoice_currency || line.currency) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="card card-pad" style={{ marginTop: 22 }}>
            <div className="row" style={{ marginBottom: 12 }}>
              <span className="card-label">รายการที่ยังไม่จับคู่</span>
              <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
                รวมรายการซ้ำที่ต้องตัดสินใจ · {number(queue.length)} รายการ
              </span>
            </div>

            {unmatched.isLoading ? <Skel h={140} /> : unmatched.isError ? (
              <div className="form-error">{errorText(unmatched.error, 'โหลดรายการที่ยังไม่จับคู่ไม่สำเร็จ')}</div>
            ) : queue.length === 0 ? (
              <Empty title="ไม่มีรายการค้าง" body="ทุกรายการในรอบนี้มีผลการจับคู่ครบแล้ว" />
            ) : (
              <div className="table-wrap">
                <table className="table data">
                  <thead>
                    <tr>
                      <th>รหัสผู้ให้บริการ</th>
                      <th>ใบแจ้งหนี้</th>
                      <th style={{ width: 130, textAlign: 'right' }}>ยอดที่ส่งมา</th>
                      <th style={{ width: 120 }}>ผล</th>
                      <th>เหตุผล</th>
                      <th style={{ width: 130, textAlign: 'right' }}>ขาด</th>
                      <th style={{ width: 120 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {queue.map((line) => (
                      <tr key={line.id}>
                        <td className="tnum" style={{ fontSize: 12 }}>{line.provider_ref}</td>
                        <td>{line.invoice_number || line.invoice_ref || '—'}</td>
                        <td className="tnum" style={{ textAlign: 'right' }}>{amountText(line.amount_satang, line.currency)}</td>
                        <td>
                          <span className="chip" style={{ color: outcomeColor(line.outcome) }}>
                            {outcomeLabel[line.outcome] ?? line.outcome}
                          </span>
                        </td>
                        <td style={{ fontSize: 12, lineHeight: 1.55 }}>{line.reason}</td>
                        <td className="tnum" style={{ textAlign: 'right', color: line.shortfall_satang > 0 ? 'var(--color-crimson)' : undefined }}>
                          {line.shortfall_satang > 0 ? amountText(line.shortfall_satang, line.invoice_currency || line.currency) : '—'}
                        </td>
                        <td>
                          <button className="btn" onClick={() => beginResolve(line)} disabled={resolveLine.isPending}>
                            จับคู่ด้วยมือ
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {resolving ? (
            <div className="card card-pad" style={{ marginTop: 22 }}>
              <div className="row" style={{ marginBottom: 8 }}>
                <span className="card-label">จับคู่ด้วยมือ</span>
                <button className="btn" style={{ marginLeft: 'auto' }} onClick={() => setResolving(null)}>
                  ยกเลิก
                </button>
              </div>
              <div className="dim" style={{ fontSize: 12, lineHeight: 1.8 }}>
                {resolving.provider_ref} · {amountText(resolving.amount_satang, resolving.currency)} ·
                ผลเดิม: {outcomeLabel[resolving.outcome] ?? resolving.outcome}
              </div>

              <div className="field" style={{ marginTop: 14 }}>
                <label htmlFor="recon-invoice">ใบแจ้งหนี้</label>
                <select
                  id="recon-invoice"
                  className="input"
                  value={invoiceID}
                  onChange={(e) => setInvoiceID(e.target.value)}
                >
                  <option value="">เลือกใบแจ้งหนี้</option>
                  {(invoices.data ?? []).map((inv) => (
                    <option key={inv.id} value={inv.id}>
                      {inv.number} · {money(inv.total_satang)} {inv.currency} · {inv.status}
                    </option>
                  ))}
                </select>
              </div>

              <div className="field" style={{ marginTop: 12 }}>
                <label htmlFor="recon-note">เหตุผลและหลักฐาน</label>
                <input
                  id="recon-note"
                  className="input"
                  value={note}
                  maxLength={500}
                  placeholder="เช่น หลักฐานโอนธนาคารยืนยันยอดขาด 7 บาท"
                  onChange={(e) => setNote(e.target.value)}
                />
              </div>

              {resolveError ? <div className="form-error" style={{ marginTop: 12 }}>{resolveError}</div> : null}
              {resolveLine.isError ? (
                <div className="form-error" style={{ marginTop: 12 }}>{errorText(resolveLine.error, 'จับคู่ด้วยมือไม่สำเร็จ')}</div>
              ) : null}

              <div className="row" style={{ gap: 8, marginTop: 16 }}>
                <button className="btn btn-primary" onClick={submitResolve} disabled={resolveLine.isPending}>
                  {resolveLine.isPending ? 'กำลังบันทึก…' : 'ยืนยันการจับคู่'}
                </button>
                <span className="dim" style={{ fontSize: 11.5, alignSelf: 'center' }}>
                  ระบบจะบันทึกผู้ใช้และเหตุผลไว้กับรายการนี้
                </span>
              </div>
            </div>
          ) : null}
        </>
      ) : null}
    </>
  )
}
